import React from 'react';

import {
  Language as LanguageIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {
  activityTemplates,
  AuthWrapper,
  ApiFile,
  BackButton,
  baseTypes,
  imageUtil,
  LittleIconWithText,
  LoadingOverlay,
  ResponsiveImage,
  routeDirectory,
  TitledChipGroup
} from 'modules';
import { FavoriteInvestorButton } from 'modules/layer1/components/InvestorSearchResultCard/components/FavoriteInvestorButton';
import {
  useLocation,
  useParams
} from 'react-router-dom';

import * as dataService from './dataService';



const imgSuccessSet = imageUtil.getUrlSet('success', 'png');



const CenteredColumnStack = styled(Stack)(() => ({
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column'
}));

const RelativeBox = styled(Box)({
  position: 'relative',
  width: '100%',
  display: 'flex',
  justifyContent: 'center'
});

const AbsoluteBox = styled(Box)({
  position: 'absolute'
});

const CenteredText = styled(Typography)(() => ({
  textAlign: 'center'
}));

const FlexGrid = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start'
});



export const InvestorProfileActivity: React.FC = props => {
  const params = useParams<{userId: string}>();
  const { data, loading, refetch } = dataService.useInvestorProfileActivity(params.userId ?? '');
  const { state } = useLocation();



  return (
    <AuthWrapper.FC>
      <activityTemplates.BaseActivityTemplate.FC>
        <LoadingOverlay active={loading} />

        <CenteredColumnStack spacing={7}>
          <RelativeBox>
            <AbsoluteBox sx={{ left: 0 }}>
              <BackButton.FC label={__getBackButtonLabel(state)} />
            </AbsoluteBox>

            <AbsoluteBox sx={{ right: 0 }}>
              <FavoriteInvestorButton
                investorUserId={params.userId ?? ''}
                isFavorited={data.isFavorited}
                onClick={() => refetch()} />
            </AbsoluteBox>

            {/* Profile picture */}
            <Card>
              <CardContent>
                <ApiFile.FC fileKey={data.profilePictureKey} />
              </CardContent>
            </Card>
          </RelativeBox>

          <CenteredColumnStack spacing={2}>
            {/* Investor info */}
            <Typography>
              {data.investmentCategory}
            </Typography>

            <Stack
              direction="row"
              spacing={1}>
              <Typography variant="h1">
                {data.companyName}
              </Typography>

              {data.isVerifiedInvestor ? <ResponsiveImage.FC urlSet={imgSuccessSet} /> : null}
            </Stack>

            <Stack
              spacing={6}
              direction="row">
              <Typography>
              Leads Investments: {data.canLeadInvestments}
              </Typography>

              <Typography>
              Check Size: {data.checkSizeMinDollars} - {data.checkSizeMaxDollars}
              </Typography>
            </Stack>

            <Stack
              spacing={6}
              direction="row">
              <LittleIconWithText.FC
                icon={LocationOnIcon}
                iconText={loading ? '' : data.address} />

              <LittleIconWithText.FC
                icon={LanguageIcon}
                iconText={loading ? '' : data.websiteUrl}
                onClick={() => {
                  window.open(data.websiteUrl, '_blank');
                }} />
            </Stack>

          </CenteredColumnStack>

          <CenteredText>
            {data.description}
          </CenteredText>

          {/* Chips area */}
          <Grid container>
            <FlexGrid
              item
              xs={4}>
              <TitledChipGroup.FC
                title="Company/Investment Stage"
                arrange="stacked"
                chipLabels={data.investmentStages}
                chipVariant="contained" />
            </FlexGrid>

            <FlexGrid
              item
              xs={4}>
              <TitledChipGroup.FC
                title="Company/Investment Area"
                chipLabels={data.investmentAreas}
                chipVariant="outlined" />

            </FlexGrid>

            <FlexGrid
              item
              xs={4}>
              <TitledChipGroup.FC
                title="Investment Geography"
                chipLabels={data.investmentGeographies}
                chipVariant="text" />
            </FlexGrid>
          </Grid>

        </CenteredColumnStack>
      </activityTemplates.BaseActivityTemplate.FC>
    </AuthWrapper.FC>
  );
};



function __getBackButtonLabel(state: unknown): string {
  if (baseTypes.isNavigationState(state) &&
    (
      state.prevPath === routeDirectory.superAdmin.dashboard.path('investors')
      || state.prevPath === routeDirectory.superAdmin.dashboard.path('startups')
    )
  ) {
    return 'Back to Dashboard';
  }
  return 'Back to Search';
}
